import { useSelector } from '@redux';
import { getConfigByName } from '@redux/config/selectors';
import { Select } from 'antd';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { useGetRootPath, useInitialFilters } from 'hooks/useShowLayout';
import { get } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DynamicObjectType } from 'types/commons';

interface IFilterLocationProps {
  keyFilter?: string;
  valueOptionKey?: string;
}

const FilterLocation = React.memo(
  ({
    keyFilter = 'locationId',
    valueOptionKey = 'id',
  }: IFilterLocationProps) => {
    const initialFilter = useInitialFilters();

    const { filter: rawFilter } = useGetFilter();
    const { rootPath, search } = useGetRootPath();
    const navigate = useNavigate();

    const channelLocations = useSelector<DynamicObjectType>((state) =>
      getConfigByName(state, 'channelLocations'),
    );

    const filter = useMemo(
      () =>
        ({
          ...rawFilter,
          q: rawFilter?.q || initialFilter?.q,
          filters: rawFilter.filters
            ? Object.values(rawFilter.filters || {})
            : Object.values(initialFilter?.filters || {}),
        }) as DynamicObjectType,
      [rawFilter, initialFilter],
    );

    const onChange = useCallback(
      (values) => {
        const searchParams = new URLSearchParams(search);
        searchParams.delete('page');
        searchParams.delete('pageSize');
        searchParams.set(
          'filters',
          JSON.stringify(
            [
              ...(filter?.filters?.filter(
                (e) => ![keyFilter].includes(e.key),
              ) ?? []),
              {
                condition: 'and',
                key: keyFilter,
                operator: 'contL',
                value: values,
              },
            ],
            null,
            0,
          ),
        );
        navigate(`${rootPath}?${searchParams.toString()}`);
      },
      [filter.filters, keyFilter, navigate, rootPath, search],
    );

    const hanldeClear = useCallback(() => {
      const searchParams = new URLSearchParams(search);
      searchParams.delete(keyFilter);
    }, [keyFilter, search]);

    const handleRootPath = useCallback(() => {
      const searchParams = new URLSearchParams(search);
      searchParams.set(
        'filters',
        JSON.stringify(
          [...filter.filters.filter((e) => ![keyFilter].includes(e.key))],
          null,
          0,
        ),
      );
      navigate(`${rootPath}?${searchParams.toString()}`);
    }, [filter.filters, keyFilter, navigate, rootPath, search]);

    const items = useMemo(() => {
      return channelLocations
        ?.filter((i) => i?.isOrderProcess)
        ?.map((item) => ({
          value: item?.[valueOptionKey],
          label: `${get(item, 'code')} - ${get(item, 'name')} `,
        }));
    }, [channelLocations, valueOptionKey]);

    const filterSort = useCallback((optionA, optionB) => {
      return (optionA?.label ?? '')
        .toLowerCase()
        .localeCompare((optionB?.label ?? '').toLowerCase());
    }, []);

    return (
      <Select
        showSearch
        options={items}
        filterSort={filterSort}
        optionFilterProp="label"
        onSelect={onChange}
        allowClear={true}
        onClear={() => {
          handleRootPath();
          hanldeClear();
        }}
        style={{ minWidth: '200px', height: 40 }}
        value={filter?.filters?.find((e) => e.key === keyFilter)?.value}
        placeholder="Location"
      />
    );
  },
);

export default FilterLocation;
